import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Footer from '../components/Footer';
import { getHomeNews, getProductCategories } from '../apis';
import { Animator, batch, FadeIn, MoveIn, MoveOut, ScrollContainer, ScrollPage } from 'react-scroll-motion';
import { appGetLocale, appPathPrefix, isMobileAspectRatio } from '../helpers/appHelper';
import { FormattedMessage } from 'react-intl';

const Home = () => {
    const appLanguage = appGetLocale();
    const isEng = appLanguage === 'en';
    const dispatch = useDispatch();
    const parent = useRef(null);
    const headerHeight = useSelector(state => state.headerHeight);
    const sectorHeight = `calc(100vh - ${headerHeight}px - 1px)`;
    const productCategories = useSelector(state => state.productCategories);
    const [homeNews, setHomeNews] = useState([]);
    const [products, setProducts] = useState([]);
    const prefix = appPathPrefix();
    const isMobile = isMobileAspectRatio();

    useEffect(() => {
        getData();
    }, []);

    useEffect(() => {
        setProducts(productCategories);
    }, [productCategories]);

    const getData = async () => {
        const [news_result, product_result] = await Promise.all([getHomeNews(), getProductCategories()]);
        if (!news_result.fail) {
            setHomeNews(news_result);
        }
        if (!product_result.fail) {
            dispatch({ type: 'SET_PRODUCT_CATEGORIES', payload: product_result });
        }
    };

    const HomeBanner = () => {
        const sliderRef = useRef(null);
        const [currentIndex, setCurrentIndex] = useState(0);
        const loop_timer = 5000;
        const [imageHeight, setImageHeight] = useState(sectorHeight);
        const buttonHeight = 50;

        const updateImageHeight = () => {
            const firstImage = sliderRef.current?.querySelector('.home-banner-image');
            if (firstImage) {
                setImageHeight(firstImage.clientHeight);
            }
        };

        useEffect(() => {
            const handleScroll = () => {
                if (sliderRef.current) {
                    const scrollLeft = sliderRef.current.scrollLeft;
                    const scrollAmount = getScrollAmount();
                    const index = Math.round(scrollLeft / scrollAmount);
                    setCurrentIndex(index);
                }
            };

            if (sliderRef.current) sliderRef.current.addEventListener('scroll', handleScroll);
            return () => {
                if (sliderRef.current) sliderRef.current.removeEventListener('scroll', handleScroll);
            };
        }, []);

        useEffect(() => {
            const interval = setInterval(() => {
                scrollNext();
            }, loop_timer);
            return () => clearInterval(interval);
        }, [currentIndex]);

        useEffect(() => {
            updateImageHeight();
            window.addEventListener('resize', updateImageHeight);
            return () => window.removeEventListener('resize', updateImageHeight);
        }, [homeNews]);

        const getScrollAmount = () => {
            if (sliderRef.current) {
                const firstImage = sliderRef.current.querySelector('.home-banner-image');
                if (firstImage) {
                    return firstImage.clientWidth;
                }
            }
            return 1430;
        };

        const scrollNext = () => {
            const scrollAmount = getScrollAmount();
            if (sliderRef.current) {
                if (currentIndex === homeNews.length - 1) {
                    sliderRef.current.scrollTo({ left: 0, behavior: 'smooth' });
                } else {
                    sliderRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
                }
            }
        };

        const scrollPrev = () => {
            const scrollAmount = getScrollAmount();
            if (sliderRef.current) {
                if (currentIndex === 0) {
                    sliderRef.current.scrollTo({ left: sliderRef.current.scrollWidth, behavior: 'smooth' });
                } else {
                    sliderRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
                }
            }
        };

        const scrollTo = (index) => {
            const scrollAmount = getScrollAmount();
            if (sliderRef.current) {
                sliderRef.current.scrollTo({ left: index * scrollAmount, behavior: 'smooth' });
            }
        };

        return (
            <div className="home-banner" style={{ height: isMobile ? "auto" : sectorHeight }}>
                {!isMobile &&
                    <>
                        <div
                            className='home-banner-bkg-left'
                            style={{
                                backgroundImage: `url(${homeNews[currentIndex]?.web_thumbnail_url})`,
                                maxHeight: imageHeight
                            }}
                        />
                        <button className="home-banner-btn-left" onClick={scrollPrev} style={{ top: `calc(${imageHeight}px / 2 - ${buttonHeight}px / 2)` }}>
                            <img src='./images/left_button.png' />
                        </button>
                    </>
                }
                <div className="home-banner-images-wrapper">
                    <a href={prefix + '/news'}>
                        <div className="home-banner-images-container" ref={sliderRef}>
                            {homeNews.map((image, index) => (
                                <img
                                    key={index}
                                    className="home-banner-image"
                                    src={!isMobile ? image?.web_thumbnail_url : image?.web_thumbnail_mobile_url}
                                    onLoad={updateImageHeight}
                                />
                            ))}
                        </div>
                    </a>
                    <div className="dots-container">
                        {homeNews.map((_, index) => (
                            <button className="dot-btn" key={index} onClick={() => scrollTo(index)}>
                                <span className={`dot ${index === currentIndex ? 'active' : ''} ${isMobile ? 'mobile-dot' : ''}`}></span>
                            </button>
                        ))}
                    </div>
                </div>
                {!isMobile &&
                    <>
                        <button className="home-banner-btn-right" onClick={scrollNext} style={{ top: `calc(${imageHeight}px / 2 - ${buttonHeight}px / 2)` }}>
                            <img src='./images/right_button.png' />
                        </button>
                        <div
                            className='home-banner-bkg-right'
                            style={{
                                backgroundImage: `url(${homeNews[currentIndex]?.web_thumbnail_url})`,
                                maxHeight: imageHeight
                            }}
                        />
                    </>
                }
            </div>
        )
    };

    const Product = () => {
        const Title = () => {
            const textStyle = {
                fontFamily: 'NotoSansTC-Bold, Noto Sans TC',
                fontWeight: 700,
                fill: '#3e3a39',
                fontSize: '40px'
            };
            const dotStyle = {
                fontFamily: 'NotoSansTC-Medium, Noto Sans TC',
                fontWeight: 500,
                fill: '#3e3a39',
                fontSize: '40px'
            };

            const TitleSVG = () => (
                isEng ?
                    <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "70%" : "25%"} viewBox={isMobile ? "0 0 524.416 150" : "0 0 524.416 353.197"}>
                        <text transform="matrix(0.829, -0.559, 0.559, 0.829, 110.554, 105.438)" fill="#3e3a39" fontSize="44" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">·</tspan></text>
                        <text transform="matrix(0.901, -0.434, 0.434, 0.901, 146.852, 81.437)" fill="#3e3a39" fontSize="44" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">P</tspan></text>
                        <text transform="matrix(0.948, -0.319, 0.319, 0.948, 173.43, 68.527)" fill="#3e3a39" fontSize="44" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">R</tspan></text>
                        <text transform="matrix(0.983, -0.186, 0.186, 0.983, 201.939, 58.71)" fill="#3e3a39" fontSize="44" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">O</tspan></text>
                        <text transform="matrix(0.999, -0.045, 0.045, 0.999, 235.479, 52.388)" fill="#3e3a39" fontSize="44" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">D</tspan></text>
                        <text transform="matrix(0.996, 0.094, -0.094, 0.996, 267.028, 50.844)" fill="#3e3a39" fontSize="44" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">U</tspan></text>
                        <text transform="matrix(0.974, 0.226, -0.226, 0.974, 300.02, 54.033)" fill="#3e3a39" fontSize="44" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">C</tspan></text>
                        <text transform="matrix(0.941, 0.339, -0.339, 0.941, 327.791, 59.407)" fill="#3e3a39" fontSize="43" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">T</tspan></text>
                        <text transform="matrix(0.899, 0.438, -0.438, 0.899, 351.918, 69.013)" fill="#3e3a39" fontSize="44" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">S</tspan></text>
                        <text transform="matrix(0.83, 0.559, -0.559, 0.83, 376.023, 79.953)" fill="#3e3a39" fontSize="44" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">·</tspan></text>
                    </svg>
                    :
                    <svg xmlns="http://www.w3.org/2000/svg" width="50%" height="50%" viewBox="0 0 305.486 101.808" preserveAspectRatio="xMidYMid meet">
                        <text transform="translate(25.35 91.794) rotate(-33.441)" style={{ ...dotStyle }}><tspan x="0" y="0">·</tspan></text>
                        <text transform="translate(59.196 69.121) rotate(-21.83)" style={{ ...textStyle }}><tspan x="0" y="0">產</tspan></text>
                        <text transform="translate(107.169 51.015) rotate(-7.154)" style={{ ...textStyle }}><tspan x="0" y="0">品</tspan></text>
                        <text transform="translate(158.139 45.628) rotate(7.289)" style={{ ...textStyle }}><tspan x="0" y="0">介</tspan></text>
                        <text transform="matrix(0.927, 0.374, -0.374, 0.927, 208.849, 53.133)" style={{ ...textStyle }}><tspan x="0" y="0">紹</tspan></text>
                        <text transform="matrix(0.833, 0.553, -0.553, 0.833, 246.721, 68.126)" style={{ ...dotStyle }}><tspan x="0" y="0">·</tspan></text>
                    </svg>
            )
            return (
                isMobile ?
                    <div className="home-product-title">
                        <TitleSVG />
                    </div>
                    :
                    <Animator className="home-product-title" animation={MoveIn(0, 200)} style={{ marginBottom: isEng? '0' : '-3%' }}>
                        <TitleSVG />
                    </Animator>
            );
        };

        const Products = () => {
            // 2025.02.10 假設最少5個產品
            const line_1 = products.slice(0, 4);
            const line_2 = products.slice(4, 8);
            const animation_value_list_in = [
                { x: -200, y: -75 },
                { x: -100, y: -25 },
                { x: 100, y: -25 },
                { x: 200, y: -75 }
            ];
            const animation_value_list_out = [
                { x: -100, y: -25 },
                { x: -200, y: -75 },
                { x: 200, y: -75 },
                { x: 100, y: -25 }
            ];
            const containerRef = useRef(null);
            const [mobileMargin, setMobileMargin] = useState(0);

            useEffect(() => {
                const updateMobileMargin = () => {
                    const firstImage = containerRef.current?.querySelector('.home-product-item.mobile img');
                    if (firstImage) {
                        setMobileMargin(containerRef.current.clientWidth - firstImage.clientWidth);
                    }
                };

                const timeoutId = setTimeout(updateMobileMargin, 100);

                window.addEventListener('resize', updateMobileMargin);
                return () => {
                    clearTimeout(timeoutId);
                    window.removeEventListener('resize', updateMobileMargin);
                };
            }, [containerRef.current]);

            const getScrollAmount = () => {
                const firstImage = containerRef.current?.querySelector('.home-product-item.mobile img');
                if (firstImage) {
                    return firstImage.clientWidth + mobileMargin / 4;
                }
                return 313;
            };

            const scrollNext = () => {
                const scrollAmount = getScrollAmount();
                if (containerRef.current) {
                    containerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
                }
            };

            const scrollPrev = () => {
                const scrollAmount = getScrollAmount();
                if (containerRef.current) {
                    containerRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
                }
            };

            return (
                <div className="home-product-containers">
                    {isMobile ?
                        <>
                            <button className="home-banner-btn-left mobile" onClick={scrollPrev}>
                                <img src='./images/left_button.png' />
                            </button>
                            <div className="home-product-container mobile" ref={containerRef}>
                                <div className="home-product-item-spacer" style={{ minWidth: mobileMargin / 2 }} />
                                {products.map((product, index) => (
                                    <div key={index} style={{ display: 'flex', flexDirection: 'row' }}>
                                        <a href={prefix + `/products?categorie_id=${product.id}`}>
                                            <div className='home-product-item mobile'>
                                                <img src={product.web_thumbnail_url} />
                                                <span>{product.name}</span>
                                            </div>
                                        </a>
                                        <div style={{ minWidth: index === products.length - 1 ? 0 : mobileMargin / 4 }} />
                                    </div>
                                ))}
                                <div className="home-product-item-spacer" style={{ minWidth: mobileMargin / 2 }} />
                            </div>
                            <button className="home-banner-btn-right mobile" onClick={scrollNext}>
                                <img src='./images/right_button.png' />
                            </button>
                        </>
                        :
                        <>
                            <div className="home-product-container">
                                {line_1.map((product, index) => (
                                    <a key={index} href={prefix + `/products?categorie_id=${product.id}`}>
                                        <Animator animation={batch(MoveIn(animation_value_list_in[index]?.x, animation_value_list_in[index]?.y), MoveOut(animation_value_list_out[index]?.x, animation_value_list_out[index]?.y))} className='home-product-item'>
                                            <img src={product.web_thumbnail_url} />
                                            <span style={{ fontSize: '1.5vw' }}>{product.name}</span>
                                        </Animator>
                                    </a>
                                ))}
                            </div>
                            <div className="home-product-container-second">
                                {line_2.map((product, index) => (
                                    <a key={index} href={prefix + `/products?categorie_id=${product.id}`}>
                                        <Animator animation={batch(MoveIn(animation_value_list_in[index]?.x, animation_value_list_in[index]?.y), MoveOut(animation_value_list_out[index]?.x, animation_value_list_out[index]?.y))} className="home-product-item">
                                            <img src={product.web_thumbnail_url} />
                                            <span style={{ fontSize: '1.5vw' }}>{product.name}</span>
                                        </Animator>
                                    </a>
                                ))}
                            </div>
                        </>
                    }
                </div>
            );
        };

        return (
            <div className="home-product">
                <Title />
                <Products />
            </div>
        )
    };

    const AboutUs = () => {

        const TitleAndContent = () => {
            return (
                isEng ?
                    <svg xmlns="http://www.w3.org/2000/svg" width="70%" height="50%" viewBox="0 0 1316 315.07">
                        <g transform="translate(230 100)">
                            <svg xmlns="http://www.w3.org/2000/svg" width="848" height="135" viewBox="0 0 848 135">
                                <text transform="translate(424 21)" fill="#fff" fontSize="20" fontFamily="NotoSans-Regular, Noto Sans"><tspan x="-417.07" y="0">Since 1984, Yamazaki Baking Co. Ltd. began as a provider of baked goods to all in Japan.  </tspan><tspan x="-327.49" y="27">Nowadays, Yamazaki is the most popular bakery brand in the country </tspan><tspan x="-293.64" y="54">and loved by everone. The company&apos;s rigorous quality control, </tspan><tspan x="-423.28" y="81">lastest technologies are designed to provide cumtomer with a wide range of tasty bread....</tspan><tspan x="0" y="108"></tspan></text>
                            </svg>
                        </g>

                        <g transform="translate(608 248.376)">
                            <g fill="rgba(255,255,255,0.22)" stroke="rgba(255,255,255,0.22)" strokeWidth="2">
                                <rect width="100.788" height="38.278" rx="19.139" stroke="none" />
                                <rect x="1" y="1" width="98.788" height="36.278" rx="18.139" fill="none" />
                            </g>
                            <a href={prefix + '/about_us'}>
                                <text transform="translate(25.946 25.623)" fill="#fff" fontSize="20" fontFamily="NotoSans-Regular, Noto Sans"><tspan x="0" y="0">More</tspan></text>
                            </a>
                        </g>
                        <g transform="matrix(1, -0.017, 0.017, 1, 419, 8.239)">
                            <text transform="matrix(0.847, -0.531, 0.531, 0.847, 105.535, 79.239)" fill="#fff" fontSize="40" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">·</tspan></text>
                            <text transform="matrix(0.88, -0.476, 0.476, 0.88, 131.31, 63.275)" fill="#fff" fontSize="40" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0"> </tspan></text>
                            <text transform="matrix(0.914, -0.405, 0.405, 0.914, 138.893, 58.766)" fill="#fff" fontSize="40" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">A</tspan></text>
                            <text transform="matrix(0.957, -0.29, 0.29, 0.957, 162.193, 48.323)" fill="#fff" fontSize="40" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">B</tspan></text>
                            <text transform="matrix(0.988, -0.155, 0.155, 0.988, 187.941, 39.291)" fill="#fff" fontSize="39" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">O</tspan></text>
                            <text transform="matrix(1, -0.019, 0.019, 1, 218.449, 35.607)" fill="#fff" fontSize="40" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">U</tspan></text>
                            <text transform="matrix(0.993, 0.119, -0.119, 0.993, 248.235, 35.27)" fill="#fff" fontSize="40" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">T</tspan></text>
                            <text transform="matrix(0.98, 0.2, -0.2, 0.98, 273.116, 38.576)" fill="#fff" fontSize="40" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0"> </tspan></text>
                            <text transform="matrix(0.957, 0.29, -0.29, 0.957, 281.791, 39.814)" fill="#fff" fontSize="40" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">U</tspan></text>
                            <text transform="matrix(0.914, 0.406, -0.406, 0.914, 309.719, 48.348)" fill="#fff" fontSize="40" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">S</tspan></text>
                            <text transform="matrix(0.88, 0.476, -0.476, 0.88, 332.535, 58.822)" fill="#fff" fontSize="40" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0"> </tspan></text>
                            <text transform="matrix(0.847, 0.531, -0.531, 0.847, 331.784, 57.451)" fill="#fff" fontSize="40" fontFamily="NotoSansSC-Bold, Noto Sans SC" fontWeight="700"><tspan x="0" y="0">·</tspan></text>
                        </g>
                    </svg>
                    :
                    <svg xmlns="http://www.w3.org/2000/svg" width="50%" height="50%" viewBox="0 0 836 270.125">
                        <g transform="translate(2924.036 -1922.31)">
                            <g transform="translate(-2732.036 1922.31)">
                                <g transform="translate(0 0)">
                                    <text transform="translate(78.928 92.359) rotate(-33.483)" fill="#fff" fontSize="40" fontFamily="NotoSansTC-Medium, Noto Sans TC" fontWeight="500"><tspan x="0" y="0">·</tspan></text>
                                    <text transform="translate(117.574 66.381) rotate(-21.874)" fill="#fff" fontSize="40" fontFamily="NotoSansTC-Medium, Noto Sans TC" fontWeight="500"><tspan x="0" y="0">關</tspan></text>
                                    <text transform="matrix(0.992, -0.125, 0.125, 0.992, 172.377, 45.587)" fill="#fff" fontSize="40" fontFamily="NotoSansTC-Medium, Noto Sans TC" fontWeight="500"><tspan x="0" y="0">於</tspan></text>
                                    <text transform="translate(230.642 39.33) rotate(7.245)" fill="#fff" fontSize="40" fontFamily="NotoSansTC-Medium, Noto Sans TC" fontWeight="500"><tspan x="0" y="0">我</tspan></text>
                                    <text transform="translate(288.636 47.815) rotate(21.916)" fill="#fff" fontSize="40" fontFamily="NotoSansTC-Medium, Noto Sans TC" fontWeight="500"><tspan x="0" y="0">們</tspan></text>
                                    <text transform="translate(331.887 64.84) rotate(33.504)" fill="#fff" fontSize="40" fontFamily="NotoSansTC-Medium, Noto Sans TC" fontWeight="500"><tspan x="0" y="0">·</tspan></text>
                                </g>
                            </g>
                            <text transform="translate(-2506.036 2057.805)" fill="#fff" fontSize="23" fontFamily="PingFangHK-Regular, PingFang HK"><tspan x="-417.312" y="0">山崎麵飽於</tspan><tspan y="0" fontFamily="NotoSans-Regular, Noto Sans">1948</tspan><tspan y="0">年在日本成立，一直以來以提供質優味美的麵包而廣受大眾歡迎，</tspan><tspan y="0" fontFamily="NotoSans-Regular, Noto Sans"></tspan><tspan x="-253" y="33">至今仍然日本人氣麵包品牌。在發展迅速的市場中，</tspan><tspan y="33" fontFamily="NotoSans-Regular, Noto Sans"></tspan><tspan x="-282.992" y="66">我們會繼續開設更多有特色的分店，研發更多新口味</tspan><tspan y="66" fontFamily="NotoSans-Regular, Noto Sans">......</tspan></text>
                            <g transform="translate(-2556.009 2146.156)">
                                <g fill="rgba(255,255,255,0.22)" stroke="rgba(255,255,255,0.22)" strokeWidth="2">
                                    <rect width="100.788" height="38.278" rx="19.139" stroke="none" />
                                    <rect x="1" y="1" width="98.788" height="36.278" rx="18.139" fill="none" />
                                </g>
                                <a href={prefix + '/about_us'}>
                                    <text id="More" transform="translate(25.946 25.623)" fill="#fff" fontSize="20" fontFamily="NotoSans-Regular, Noto Sans"><tspan x="0" y="0">More</tspan></text>
                                </a>
                            </g>
                        </g>
                    </svg>
            );
        }
        return (
            isMobile ?
            <div className="home-mobile-about-us">
                <div className="home-mobile-more" onClick={() => window.location.href = prefix + '/about_us'}>
                    {/* 2025.04.07 Remove svg because mobile_home_about-us including "more" */}
                    {/* <svg id="mobile_more" xmlns="http://www.w3.org/2000/svg" width="78.894" height="29.963" viewBox="0 0 78.894 29.963">
                        <g fill="rgba(255,255,255,0.69)" stroke="rgba(255,255,255,0.22)" strokeWidth="2">
                            <rect width="78.894" height="29.963" rx="14.982" stroke="none" />
                            <rect x="1" y="1" width="76.894" height="27.963" rx="13.982" fill="none" />
                        </g>
                        <text transform="translate(20.947 20.623)" fill="#431515" fontSize="15" fontFamily="NotoSans-Regular, Noto Sans">
                            <tspan>More</tspan>
                        </text>
                    </svg> */}
                </div>
                {isEng?
                <img src='./images/mobile_home_about-us-en.png' className='home-mobile-about-us-bkg' />
                :
                <img src='./images/mobile_home_about-us.png' className='home-mobile-about-us-bkg' />
                }
            </div>
            :
            // 波浪background，保留作參考
            // <>
            //     <div style={{ background: 'linear-gradient(180deg,#F8F6F4 50%,rgb(52, 100, 166) 50%)', height: '4vh' }}>
            //         <svg viewBox="0 0 1920 40" height="100%" width="100%">
            //             <rect width="960" height="40" fill="#F8F6F4" />
            //             <path d="M 0 20 C 0 20 0 20 960 20 C 1440 0 1440 0 1920 20 L 1920 20 L 0 20" fill='#3464A6'></path>
            //             <rect width="1920" height="40" y="20" fill="#3464A6" />
            //             <path d="M 0 20 C 480 40 480 40 960 20 L 1920 20 L 0 20" fill='#F8F6F4'></path>
            //         </svg>
            //     </div>
            //     <div className="home-about-us-bkg">
            //         <Animator animation={batch(MoveIn(0, 200), MoveOut(0, -200))} className="home-about-us">
            //             <TitleAndContent />
            //         </Animator>
            //     </div>
            //     <div style={{ background: 'linear-gradient(180deg,rgb(52, 100, 166) 50%,#F8F6F4 50%)', height: '4vh' }}>
            //         <svg viewBox="0 0 1920 40" height="100%" width="100%">
            //             <rect width="1920" height="40" y="20" fill="#F8F6F4" />
            //             <path d="M 0 20 C 480 0 480 0 960 20 L 1920 20 L 0 20" fill='#F8F6F4'></path>
            //             <path d="M 0 20 C 0 20 0 20 960 20 C 1440 40 1440 40 1920 20 L 1920 20 L 0 20" fill='#3464A6'></path>
            //         </svg>
            //     </div>
            // </>
            <div className="home-about-us-bkg">
                <Animator animation={batch(MoveIn(0, 200), MoveOut(0, -200))} className="home-about-us">
                    <TitleAndContent />
                </Animator>
            </div>
        )
    };

    const RecruitAndShops = () => {
        return (
            isMobile ?
                <div className='home-recruit-and-shops'>
                    <div>
                        <a href={prefix + '/stores'}>
                            {isEng ?
                            <img src='./images/home_shops2_en.png' className='home-shops-img' />
                            :
                            <img src='./images/home_shops2.png' className='home-shops-img' />
                            }
                        </a>
                    </div>
                    <div>
                        <a href={prefix + '/careers'}>
                            {isEng ?
                            <img src='./images/home_recruit2_en.png' className='home-recruit-img' />
                            :
                            <img src='./images/home_recruit2.png' className='home-recruit-img' />
                            }
                        </a>
                    </div>
                </div>
                :
                <div className='home-recruit-and-shops'>
                    <Animator animation={batch(MoveIn(-100, 0), FadeIn(0, 1))}>
                        <a href={prefix + '/careers'}>
                            {isEng ?
                            <div style={{position: 'relative'}}>
                                <img src='./images/home_recruit2_en.png' className='home-recruit-img' />
                                <img src='./images/home_recruit2_en.png' className='home-recruit-img-hover' />
                            </div>
                            :
                            <div style={{position: 'relative'}}>
                                <img src='./images/home_recruit2.png' className='home-recruit-img' />
                                <img src='./images/home_recruit2.png' className='home-recruit-img-hover' />
                            </div>
                            }
                        </a>
                    </Animator>
                    <Animator animation={batch(MoveIn(100, 0), FadeIn(0, 1))}>
                        <a href={prefix + '/stores'}>
                            {isEng ?
                            <div style={{position: 'relative'}}>
                                <img src='./images/home_shops2_en.png' className='home-shops-img' />
                                <img src='./images/home_shops2_en.png' className='home-shops-img-hover' />
                            </div>
                            :
                            <div style={{position: 'relative'}}>
                                <img src='./images/home_shops2.png' className='home-shops-img' />
                                <img src='./images/home_shops2.png' className='home-shops-img-hover' />
                            </div>
                            }
                        </a>
                    </Animator>
                </div>
        )
    };

    const MobileIcon = () => {

        return (
            <>
                <div className='home-mobile-icon-list'>
                    <div className='home-mobile-icon'>
                        <a href={prefix + '/news'}>
                            <img src='./images/menu-news.png' />
                            <FormattedMessage id="header.news" />
                        </a>
                    </div>
                    <div className='home-mobile-icon'>
                        <a href={prefix + '/products'}>
                            <img src='./images/menu-product.png' />
                            <FormattedMessage id="header.product" />
                        </a>
                    </div>
                    <div className='home-mobile-icon'>
                        <a href={prefix + '/stores'}>
                            <img src='./images/menu-shop.png' />
                            <FormattedMessage id="header.shops" />
                        </a>
                    </div>
                </div>
                <div className='home-mobile-dot-list'>
                    {Array.from({ length: 31 }).map((_, index) => (
                        <span key={index} className="dot mobile" />
                    ))}
                </div>
            </>
        )
    };

    return (
        isMobile ?
            <div className='home' ref={parent}>
                <div>
                    <div className='home-sector' style={{ height: 'auto', flexDirection: 'column' }}>
                        <HomeBanner />
                        <MobileIcon />
                    </div>
                    <div className='home-sector'>
                        <Product />
                    </div>
                    <div className='home-sector' style={{ flexDirection: 'column' }}>
                        <RecruitAndShops />
                    </div>
                    <div className='home-sector' style={{ backgroundColor: '#3464A6', flexDirection: 'column' }}>
                        <AboutUs />
                    </div>
                    <div className='home-sector' style={{ backgroundColor: '#3464A6', flexDirection: 'column', marginTop: '-4px' }}>
                        <Footer />
                    </div>
                </div>
            </div>
            :
            <div className='home' ref={parent} style={{ height: sectorHeight }}>
                <ScrollContainer snap={"mandatory"} scrollParent={parent.current} style={{ height: sectorHeight }} simpleHeight={headerHeight}>
                    <ScrollPage className='home-sector' style={{ height: sectorHeight }}>
                        <HomeBanner />
                    </ScrollPage>
                    <ScrollPage className='home-sector' style={{ height: sectorHeight }}>
                        <Product />
                    </ScrollPage>
                    <ScrollPage className='home-sector' style={{ height: sectorHeight, flexDirection: 'column' }}>
                        <AboutUs />
                    </ScrollPage>
                    <ScrollPage className='home-sector' style={{ height: sectorHeight, flexDirection: 'column' }}>
                        <RecruitAndShops />
                        <Footer />
                    </ScrollPage>
                </ScrollContainer>
            </div>
    );
};

export default Home;